import { initializeApp } from 'firebase/app';
import {
  initializeAuth, indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence,
  RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider,
  signInWithCredential, signInWithCustomToken, onAuthStateChanged, signOut,
} from 'firebase/auth';
import {
  initializeFirestore, persistentLocalCache, persistentMultipleTabManager,
  collection, doc, getDoc, getDocFromServer, getDocs, setDoc, updateDoc, onSnapshot,
  query, where, orderBy, serverTimestamp, addDoc, limit, deleteField, Timestamp, runTransaction, FieldPath,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, CustomProvider } from 'firebase/app-check';
import { getDatabase, ref, update, onValue, get as rtdbGet } from 'firebase/database';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { getAnalytics, logEvent, isSupported as isAnalyticsSupported } from 'firebase/analytics';
import { getPerformance } from 'firebase/performance';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { shouldUseNativePhoneAuth } from '../utils/platform';
import { buildCreateRidePayload } from '../utils/createRidePayload';
import { parseRideQuote } from '../utils/rideQuote';
import { nextPushDevices, withoutPushDevice } from '../utils/pushDevices';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-CZ6Q4LKSR8',
};

const app = initializeApp(firebaseConfig);

// --- App Check --------------------------------------------------------------
// App Check is currently NOT enforced on the callables (enforceAppCheck was
// removed) — the SDK still mints & attaches tokens, and callables/Firestore
// still accept a placeholder if attestation is slow. Keep it wired so we can
// re-enforce later without a client change.
//   - Native (Capacitor Android): Play Integrity via @capacitor-firebase/app-check,
//     bridged into the JS SDK with a CustomProvider so WebView requests carry
//     X-Firebase-AppCheck.
//   - Web: reCAPTCHA Enterprise via the Firebase JS SDK.
const APPCHECK_ENABLED = ['1', 'true'].includes(
  String(process.env.REACT_APP_APPCHECK_ENABLED || '').toLowerCase(),
);
const initAppCheck = () => {
  if (!APPCHECK_ENABLED) return;
  try {
    if (Capacitor.isNativePlatform()) {
      // Play Integrity can only attest an app delivered by Play. For a locally
      // installed debug build set REACT_APP_APPCHECK_DEBUG=1 — the plugin logs a
      // debug token to logcat; register it under Firebase Console -> App Check
      // -> Apps -> Manage debug tokens.
      const nativeProvider = process.env.REACT_APP_APPCHECK_DEBUG === '1' ? 'debug' : 'playIntegrity';
      // Load the plugin + initialize lazily & memoized, so the provider below
      // registers synchronously (before getFirestore/getDatabase/getStorage) —
      // otherwise the first requests race the provider and go out unverified.
      //
      // The plugin object must never be a promise's result: Capacitor plugin
      // objects answer ANY property, including `then`, so the promise tries to
      // unwrap it and fails ("FirebaseAppCheck.then() is not implemented").
      // That made every token request wait out the 2 s timeout below, and Auth
      // waits for a token before its first call: ~2 s on every cold start, and
      // no real App Check token ever. So the plugin lives in a plain box and the
      // loader resolves to nothing (see capacitor plugin thenable trap).
      const nativeBox = { plugin: null };
      let nativeLoading = null;
      const loadNativePlugin = () => {
        if (nativeBox.plugin) return Promise.resolve();
        if (!nativeLoading) {
          nativeLoading = import('@capacitor-firebase/app-check')
            .then(async (mod) => {
              await mod.FirebaseAppCheck.initialize({
                provider: nativeProvider,
                isTokenAutoRefreshEnabled: true,
              });
              nativeBox.plugin = mod.FirebaseAppCheck;
            })
            .catch((e) => { nativeLoading = null; throw e; });
        }
        return nativeLoading;
      };
      const nativeGetToken = async () => {
        await loadNativePlugin();
        return nativeBox.plugin.getToken();
      };
      initializeAppCheck(app, {
        provider: new CustomProvider({
          // Bounded: Play Integrity can stall for seconds (especially right after
          // a Play release, before Google recognises the new build). Never let a
          // token fetch hang an Auth / Firestore / RTDB request — after the
          // timeout we reject and the SDK proceeds with an unverified request
          // instead of blocking (a hung promise is unrecoverable; a rejection is
          // not). 2s is enough once the plugin is warm (see prewarm below);
          // App Check is unenforced so a placeholder token still works. If you
          // re-enforce, raise this and add a staged rollout.
          getToken: async () => {
            const res = await Promise.race([
              nativeGetToken(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('appcheck_timeout')), 2000)),
            ]);
            return { token: res.token, expireTimeMillis: res.expireTimeMillis };
          },
        }),
        isTokenAutoRefreshEnabled: true,
      });
      // Prewarm: kick off plugin init + the first Play Integrity attestation now,
      // during app startup — so the token is already cached when the first
      // callable (Truecaller login) needs it, instead of that request waiting
      // seconds for a cold attestation.
      nativeGetToken().catch(() => {});
      return;
    }

    // Web
    const appCheckKey = process.env.REACT_APP_FIREBASE_APPCHECK_KEY;
    const appCheckDebugToken = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
    if (appCheckDebugToken && typeof self !== 'undefined') {
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken === 'true' ? true : appCheckDebugToken;
    }
    if (appCheckKey) {
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(appCheckKey),
        isTokenAutoRefreshEnabled: true,
      });
    } else {
      console.warn('App Check: REACT_APP_FIREBASE_APPCHECK_KEY not set — callable/Firestore requests will be rejected while enforcement is on.');
    }
  } catch (e) {
    console.error('App Check init failed:', e?.message || e);
  }
};
initAppCheck();

let analytics = null;
isAnalyticsSupported().then((supported) => {
  if (supported) {
    try { analytics = getAnalytics(app); } catch { /* analytics blocked/disabled */ }
  }
}).catch(() => {});

// Auto-instruments page load + network request timing — no manual traces
// needed for baseline data. WebView network calls (Firestore/RTDB/Functions/
// Maps) are made from JS, so this is what actually sees them — the native
// Android Performance plugin only sees native OkHttp traffic, which this app
// has almost none of.
try { getPerformance(app); } catch { /* perf blocked/disabled */ }

export const logAnalyticsEvent = (eventName, params = {}) => {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('mt_analytics_opt_out') === '1') {
      return;
    }
  } catch { /* */ }
  if (analytics) {
    try { logEvent(analytics, eventName, params); } catch { /* */ }
  }
};
// Same as getAuth(app) minus the popup/redirect sign-in helper: we never use
// Google/Facebook popup or redirect sign-in (OTP, Truecaller custom token and
// native phone credential only), and that helper loaded a firebaseapp.com
// iframe on every start that held the splash ~1.25 s before the login check
// could begin. The persistence list is exactly getAuth's, in the same order,
// so the saved login is found and nobody is logged out. Must stay the first
// Auth setup: a getAuth() before this would make it throw (the plugin's web
// helper only calls getAuth() lazily, after this has run).
// If popup/redirect sign-in is ever added, pass browserPopupRedirectResolver
// to that call (signInWithPopup(auth, provider, browserPopupRedirectResolver)).
export const auth = initializeAuth(app, {
  persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
});
/**
 * Long-polling is more reliable in Android WebView (avoids stalled ride status).
 * Persistent (IndexedDB-backed) local cache means config/platform, recent rides,
 * etc. survive a full app close instead of starting from nothing every cold
 * launch — onSnapshot delivers the last-known document instantly while the
 * real listener reconnects in the background, instead of every screen that
 * waits on Firestore data blocking on a fresh network round-trip each time.
 * Multi-tab manager since this same code also serves the plain website.
 */
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
export const rtdb = getDatabase(app);
export const functions = getFunctions(app, 'asia-south1');

export const PUBLIC_ORIGIN = () =>
  process.env.REACT_APP_PUBLIC_ORIGIN
  || (typeof window !== 'undefined' ? window.location.origin : 'https://rides.mytownly.in');

export const trackUrlForToken = (token) =>
  token ? `${PUBLIC_ORIGIN().replace(/\/$/, '')}/track/${token}` : '';

// OTP / share tokens are minted by Cloud Functions only.

export const getFirebaseMessaging = () => getMessaging(app);

let firebaseAuthNativePlugin = null;
const getNativeAuthPlugin = () => {
  if (!firebaseAuthNativePlugin) {
    firebaseAuthNativePlugin = registerPlugin('FirebaseAuthentication');
  }
  return firebaseAuthNativePlugin;
};

const getPhoneAuthPlugin = () => (
  shouldUseNativePhoneAuth() ? getNativeAuthPlugin() : FirebaseAuthentication
);

const nativePhoneAuthCall = async (method, options) => {
  if (shouldUseNativePhoneAuth() && typeof Capacitor.nativePromise === 'function') {
    return Capacitor.nativePromise('FirebaseAuthentication', method, options);
  }
  return getPhoneAuthPlugin()[method](options);
};

let nativeVerificationId = null;
let nativePhoneError = null;
let nativePhoneListenersReady = false;
let nativeOtpAttempt = 0;
let nativeOtpCallbacks = { onAutoVerified: null, onCodeReceived: null };

export const setNativeOtpCallbacks = (callbacks = {}) => {
  nativeOtpCallbacks = { ...nativeOtpCallbacks, ...callbacks };
};

const AUTH_VID_KEY = 'mt_rides_auth_vid';
const AUTH_PHONE_KEY = 'mt_rides_auth_phone';

const storeVerificationSession = (verificationId, phoneNumber) => {
  if (!verificationId) return;
  try {
    sessionStorage.setItem(AUTH_VID_KEY, verificationId);
    localStorage.setItem(AUTH_VID_KEY, verificationId);
    if (phoneNumber) {
      sessionStorage.setItem(AUTH_PHONE_KEY, phoneNumber);
      localStorage.setItem(AUTH_PHONE_KEY, phoneNumber);
    }
  } catch { /* private mode */ }
};

const getStoredVerificationId = () => {
  try {
    return sessionStorage.getItem(AUTH_VID_KEY)
      || localStorage.getItem(AUTH_VID_KEY)
      || null;
  } catch {
    return null;
  }
};

export const getPendingOtpPhone = () => {
  try {
    return sessionStorage.getItem(AUTH_PHONE_KEY)
      || localStorage.getItem(AUTH_PHONE_KEY)
      || '';
  } catch {
    return '';
  }
};

export const hasPendingOtpSession = () => Boolean(
  nativeVerificationId || getStoredVerificationId(),
);

export const getPendingVerificationId = () => getStoredVerificationId() || '';

export const waitForSignedInUser = (maxMs = 8000) => waitForAuthUser(maxMs);

const clearVerificationSession = () => {
  nativeVerificationId = null;
  try {
    sessionStorage.removeItem(AUTH_VID_KEY);
    sessionStorage.removeItem(AUTH_PHONE_KEY);
    localStorage.removeItem(AUTH_VID_KEY);
    localStorage.removeItem(AUTH_PHONE_KEY);
  } catch { /* */ }
};

const rememberVerificationId = (verificationId, phoneNumber) => {
  nativeVerificationId = verificationId;
  storeVerificationSession(verificationId, phoneNumber);
};

const waitForVerificationId = (maxMs = 8000) => new Promise((resolve) => {
  const existing = nativeVerificationId || getStoredVerificationId();
  if (existing) {
    resolve(existing);
    return;
  }
  const started = Date.now();
  const poll = setInterval(() => {
    const id = nativeVerificationId || getStoredVerificationId();
    if (id) {
      clearInterval(poll);
      resolve(id);
    } else if (Date.now() - started > maxMs) {
      clearInterval(poll);
      resolve(null);
    }
  }, 200);
});

const waitForAuthUser = (maxMs = 4000) => new Promise((resolve) => {
  if (auth.currentUser) {
    resolve(auth.currentUser);
    return;
  }
  const timeout = setTimeout(() => {
    unsub();
    resolve(null);
  }, maxMs);
  const unsub = onAuthStateChanged(auth, (user) => {
    if (user) {
      clearTimeout(timeout);
      unsub();
      resolve(user);
    }
  });
});

const signInWithNativeCredential = async (verificationId, code) => {
  const credential = PhoneAuthProvider.credential(verificationId, code);
  const result = await signInWithCredential(auth, credential);
  clearVerificationSession();
  return result.user;
};

const waitForNativeVerificationId = (attemptId) => new Promise((resolve, reject) => {
  const started = Date.now();
  const poll = setInterval(() => {
    if (attemptId !== nativeOtpAttempt) {
      clearInterval(poll);
      reject(Object.assign(new Error('OTP request superseded'), { code: 'auth/superseded' }));
      return;
    }
    if (nativeVerificationId) {
      clearInterval(poll);
      resolve({ verificationId: nativeVerificationId });
      return;
    }
    if (auth.currentUser) {
      clearInterval(poll);
      resolve({ autoVerified: true });
      return;
    }
    if (nativePhoneError) {
      clearInterval(poll);
      const err = nativePhoneError;
      nativePhoneError = null;
      reject(err);
    } else if (Date.now() - started > 90000) {
      clearInterval(poll);
      reject(Object.assign(new Error('OTP request timed out'), { code: 'auth/timeout' }));
    }
  }, 150);
});

let nativePhonePendingPhone = null;

const ensureNativePhoneListeners = async () => {
  if (nativePhoneListenersReady || !shouldUseNativePhoneAuth()) return;
  const plugin = getNativeAuthPlugin();
  await Promise.all([
    plugin.addListener('phoneCodeSent', (event) => {
      const phone = nativePhonePendingPhone || getPendingOtpPhone();
      rememberVerificationId(event?.verificationId, phone);
    }),
    plugin.addListener('phoneVerificationCompleted', async (event) => {
      // With skipNativeAuth: true, native must NOT consume the SMS code.
      // Sign in only on the WebView Firebase Auth so React sees the user.
      const code = event?.verificationCode;
      if (code) {
        const vid = nativeVerificationId || getStoredVerificationId() || await waitForVerificationId(8000);
        if (!vid) {
          nativeOtpCallbacks.onCodeReceived?.(code);
          return;
        }
        try {
          if (auth.currentUser) {
            clearVerificationSession();
            nativeOtpCallbacks.onAutoVerified?.();
            return;
          }
          await signInWithNativeCredential(vid, code);
          nativeOtpCallbacks.onAutoVerified?.();
        } catch (e) {
          // Code already used / race — if JS auth somehow has a user, continue.
          if (auth.currentUser) {
            clearVerificationSession();
            nativeOtpCallbacks.onAutoVerified?.();
            return;
          }
          // Let the user type the code once; don't poison sendOtp waiters.
          nativeOtpCallbacks.onCodeReceived?.(code);
        }
        return;
      }
      const user = await waitForAuthUser(8000);
      if (user) {
        clearVerificationSession();
        nativeOtpCallbacks.onAutoVerified?.();
      }
    }),
    plugin.addListener('phoneVerificationFailed', (event) => {
      const raw = event?.message || event?.code || 'Phone verification failed';
      nativePhoneError = Object.assign(new Error(raw), {
        code: event?.code || raw,
      });
    }),
    plugin.addListener('authStateChange', async (event) => {
      if (event?.user) {
        await waitForAuthUser(3000);
      }
    }),
  ]);
  nativePhoneListenersReady = true;
};

export const initNativePhoneAuth = () => {
  if (shouldUseNativePhoneAuth()) {
    ensureNativePhoneListeners().catch((e) => console.warn('Native phone auth init:', e?.message));
  }
};

const AUTH_ERROR_KEYS = {
  'auth/invalid-phone-number': 'authErrors.invalidPhone',
  'auth/too-many-requests': 'authErrors.tooManyRequests',
  'auth/captcha-check-failed': 'authErrors.captchaFailed',
  'auth/argument-error': 'authErrors.captchaFailed',
  'auth/invalid-verification-code': 'authErrors.invalidOtp',
  'auth/code-expired': 'authErrors.codeExpired',
  'auth/missing-verification-code': 'authErrors.missingOtp',
  'auth/quota-exceeded': 'authErrors.quotaExceeded',
  'auth/app-not-authorized': 'authErrors.appNotAuthorized',
  'auth/operation-not-allowed': 'authErrors.phoneNotEnabled',
  'auth/network-request-failed': 'authErrors.networkFailed',
  'auth/timeout': 'authErrors.timeout',
  'auth/native-otp-failed': 'authErrors.nativeOtpFailed',
  'auth/auto-verify-failed': 'authErrors.nativeOtpFailed',
};

const NATIVE_AUTH_HINTS = [
  'invalid-cert-hash', 'invalid cert', 'not authorized', 'app identifier',
  'missing a valid app', '17499', '17093', '12500', 'app-not-authorized',
  'developer_error', 'api_key', 'play_integrity',
];

export const getAuthErrorKey = (error) => {
  const code = error?.code || '';
  const msg = error?.message || '';
  const combined = `${code} ${msg}`.toLowerCase();
  if (msg.includes('region enabled')) return 'authErrors.regionNotEnabled';
  if (AUTH_ERROR_KEYS[code]) return AUTH_ERROR_KEYS[code];
  if (NATIVE_AUTH_HINTS.some((hint) => combined.includes(hint))) {
    return 'authErrors.appNotAuthorized';
  }
  if (code.includes('requests-from-referer') || combined.includes('referer')) {
    return 'authErrors.refererBlocked';
  }
  if (code === 'auth/network-request-failed' || msg.includes('Failed to fetch') || msg.includes('ERR_FAILED')) {
    return 'authErrors.networkFailed';
  }
  if (msg.includes('No OTP session') || code === 'auth/missing-otp-session') {
    return 'authErrors.noOtpSession';
  }
  if (combined.includes('timeout')) return 'authErrors.timeout';
  if (combined.includes('too-many') || combined.includes('too many')) return 'authErrors.tooManyRequests';
  if (combined.includes('quota')) return 'authErrors.quotaExceeded';
  if (combined.includes('recaptcha')) return 'authErrors.captchaFailed';
  return 'authErrors.default';
};

/** Short code for support / debugging (shown in login error toast on native). */
export const getAuthErrorDebug = (error) => {
  const code = error?.code;
  if (code && code !== 'auth/default') return code.replace(/^auth\//, '');
  const msg = (error?.message || '').slice(0, 48);
  return msg || 'unknown';
};

const ensureRecaptchaContainer = () => {
  let el = document.getElementById('recaptcha-container');
  if (!el) {
    el = document.createElement('div');
    el.id = 'recaptcha-container';
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = 'position:fixed;left:-9999px;bottom:0;width:1px;height:1px;overflow:hidden';
    document.body.appendChild(el);
  }
  return el;
};

const getRecaptchaVerifier = async () => {
  ensureRecaptchaContainer();
  if (window.recaptchaVerifier) {
    return window.recaptchaVerifier;
  }
  window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
    size: 'invisible',
    callback: () => {},
    'expired-callback': () => {
      window.recaptchaVerifier = null;
      window.confirmationResult = null;
    },
  });
  await window.recaptchaVerifier.render();
  return window.recaptchaVerifier;
};

export const prewarmRecaptcha = () => {
  if (shouldUseNativePhoneAuth()) return;
  try {
    if (typeof window !== 'undefined') {
      getRecaptchaVerifier().catch(() => {});
    }
  } catch { /* ignore */ }
};

/**
 * Server-side cost guard, called before Firebase Auth is ever asked to send
 * a real SMS (~Rs.6.69/message). Firebase's own phone-auth send doesn't run
 * through our backend, so this is a separate check up front rather than
 * something wrapped around it. A rejection throws with "too many" in the
 * message, which getAuthErrorKey already maps to authErrors.tooManyRequests
 * — same toast a real Firebase rate-limit shows, no new UI path needed.
 */
const checkOtpCooldown = async (phoneNumber) => {
  const phoneDigits = String(phoneNumber || '').replace(/\D/g, '').slice(-10);
  await httpsCallable(functions, 'checkOtpCooldown')({ phone: phoneDigits });
};

export const sendOtp = async (phoneNumber) => {
  await checkOtpCooldown(phoneNumber);
  window.confirmationResult = null;
  if (shouldUseNativePhoneAuth()) {
    try {
      await ensureNativePhoneListeners();
      clearVerificationSession();
      nativeOtpAttempt += 1;
      const attemptId = nativeOtpAttempt;
      nativePhoneError = null;
      nativePhonePendingPhone = phoneNumber;
      // skipNativeAuth: keep OTP unused until WebView signInWithCredential (avoids code-expired).
      await nativePhoneAuthCall('signInWithPhoneNumber', {
        phoneNumber,
        timeout: 120,
        skipNativeAuth: true,
      });
      const waitResult = await waitForNativeVerificationId(attemptId);
      if (waitResult.autoVerified) {
        const user = await waitForAuthUser(8000);
        if (user) {
          clearVerificationSession();
          return { autoVerified: true };
        }
      }
      const verificationId = nativeVerificationId || waitResult?.verificationId || '';
      if (verificationId) {
        storeVerificationSession(verificationId, phoneNumber);
        return { native: true, verificationId };
      }
      throw Object.assign(new Error('Native OTP session missing'), { code: 'auth/missing-otp-session' });
    } catch (e) {
      console.warn('Native OTP failed:', e?.message || e);
      throw e;
    } finally {
      nativePhonePendingPhone = null;
    }
  }

  try {
    const verifier = await getRecaptchaVerifier();
    window.confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier);
    return { web: true };
  } catch (e) {
    console.error('sendOtp failed:', e?.code, e?.message, e);
    throw e;
  }
};

const isOtpVerifyFatal = (error) => {
  const code = String(error?.code || '').toLowerCase();
  const msg = String(error?.message || '').toLowerCase();
  const combined = `${code} ${msg}`;
  return combined.includes('code-expired')
    || combined.includes('session-expired')
    || combined.includes('invalid-verification-code')
    || combined.includes('invalid verification code');
};

export const verifyOtp = async (otp, preferredVerificationId = '') => {
  if (auth.currentUser) {
    return auth.currentUser;
  }
  if (shouldUseNativePhoneAuth()) {
    let verificationId = String(preferredVerificationId || '').trim()
      || nativeVerificationId
      || getStoredVerificationId();
    if (!verificationId) {
      verificationId = await waitForVerificationId(15000);
    }
    if (!verificationId) {
      throw Object.assign(new Error('No OTP session'), { code: 'auth/missing-otp-session' });
    }
    // Sign in on the web/JS layer only. Confirming on the native layer first would
    // consume the one-time code while React auth state (router guards) stays signed out.
    try {
      return await signInWithNativeCredential(verificationId, otp);
    } catch (credErr) {
      if (isOtpVerifyFatal(credErr)) {
        clearVerificationSession();
      }
      throw credErr;
    }
  }
  if (!window.confirmationResult) {
    if (auth.currentUser) return auth.currentUser;
    throw Object.assign(new Error('No OTP session'), { code: 'auth/missing-otp-session' });
  }
  try {
    const result = await window.confirmationResult.confirm(otp);
    return result.user;
  } catch (err) {
    if (auth.currentUser) return auth.currentUser;
    throw err;
  }
};

/**
 * On logout: remove only THIS device's notification entry. The single
 * fcmToken is cleared only when it is this device's, so logging out on the
 * website or a second phone no longer silences the rider's other phone.
 * `device` = { deviceId, token } (utils/pushDevices.js); without it, the old
 * behaviour (clear fcmToken).
 */
export const clearFcmToken = async (uid, device = null) => {
  if (!uid) return;
  const ref = doc(db, 'users', uid);
  try {
    if (!device?.deviceId) {
      await updateDoc(ref, { fcmToken: deleteField(), updatedAt: serverTimestamp() });
      return;
    }
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const mine = device.token || data.pushDevices?.[device.deviceId]?.token || null;
      const patch = { pushDevices: withoutPushDevice(data.pushDevices, device.deviceId), updatedAt: serverTimestamp() };
      if (mine && data.fcmToken === mine) patch.fcmToken = deleteField();
      tx.update(ref, patch);
    });
  } catch (e) {
    // A transaction can't run offline. Privacy first: a plain write (queued
    // until online) removes this device's entry and, as before, fcmToken, so a
    // logged-out phone never keeps receiving this rider's alerts and trip PIN.
    console.warn('clearFcmToken transaction failed, using a plain write:', e?.message);
    if (!device?.deviceId) return;
    try {
      await updateDoc(ref, new FieldPath('pushDevices', device.deviceId), deleteField(),
        'fcmToken', deleteField(), 'updatedAt', serverTimestamp());
    } catch (e2) {
      console.warn('clearFcmToken failed:', e2?.message);
    }
  }
};

export const logout = async () => {
  clearVerificationSession();
  const uid = auth.currentUser?.uid;
  if (uid) {
    try {
      // Drop this device's push token so a different passenger logging in on the
      // same device doesn't inherit A's ride alerts (pickup/drop/OTP).
      const { clearPassengerMessaging } = await import('./notifications');
      await clearPassengerMessaging(uid);
    } catch { /* best-effort */ }
  }
  return signOut(auth);
};

export const prewarmTruecallerFunction = () => {
  try {
    const fn = httpsCallable(functions, 'verifyTruecallerToken');
    fn({ warmup: true }).catch(() => {});
  } catch {
    // Fire-and-forget prewarm
  }
};

export const loginWithTruecallerToken = async (customToken) => {
  const result = await signInWithCustomToken(auth, customToken);
  clearVerificationSession();
  return result.user;
};

export const verifyTruecallerPayload = async (payload) => {
  const fn = httpsCallable(functions, 'verifyTruecallerToken');
  const res = await fn({ payload });
  // Never discard a login key the server already issued: the rider is verified, and
  // dropping it would push them to a paid SMS OTP. (The timeout policy lives in
  // utils/truecallerSignIn.js; this function no longer second-guesses it.)
  if (!res.data?.customToken) throw new Error('Truecaller custom token missing');
  const user = await loginWithTruecallerToken(res.data.customToken);
  return {
    user,
    profile: {
      id: res.data?.uid || user.uid,
      phone: res.data?.phone || user.phoneNumber || '',
      name: (res.data?.name || '').trim(),
    },
  };
};

const withAuthRetry = async (fn, maxRetries = 4, delayMs = 350) => {
  let lastErr = null;
  for (let i = 0; i < maxRetries; i += 1) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const code = String(err?.code || '').toLowerCase();
      const msg = String(err?.message || '').toLowerCase();
      if (code.includes('permission-denied') || msg.includes('permission') || code.includes('unavailable')) {
        await new Promise((r) => setTimeout(r, delayMs * (i + 1)));
      } else {
        throw err;
      }
    }
  }
  throw lastErr;
};

export const getUser = async (uid) => {
  return withAuthRetry(async () => {
    const snap = await getDoc(doc(db, 'users', uid));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  });
};

export const getUserFast = async (uid) => {
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch {
    return null;
  }
};

export const ensureUserDefaults = async (uid, phone) => {
  return withAuthRetry(async () => {
    let existing = await getUser(uid);

    // No profile under this uid, but this phone already has one under an old uid
    // (rare — phone re-verification minted a new Auth uid)? Re-link it server-side
    // so ride history / saved places / consent aren't orphaned. Carries safety
    // flags forward so re-verifying can't shed a block.
    if (!existing) {
      try {
        const res = await httpsCallable(functions, 'linkPassengerProfile')({});
        if (res.data?.linked) existing = await getUser(uid);
      } catch (e) {
        console.warn('linkPassengerProfile failed (non-fatal):', e?.message || e);
      }
    }

    const patch = {
      phone: phone || existing?.phone || '',
      updatedAt: serverTimestamp(),
    };
    if (!existing) {
      patch.profileComplete = false;
      patch.walletBalance = 0;
      patch.ratingAvg = 5;
      patch.ratingCount = 0;
      // Explicit false → show first-login walkthrough once (existing users omit this field).
      patch.tourDone = false;
      patch.createdAt = serverTimestamp();
    } else {
      if (existing.name?.trim() && existing.profileComplete === false) {
        patch.profileComplete = true;
      }
      if (existing.ratingAvg == null) patch.ratingAvg = 5;
      if (existing.ratingCount == null) patch.ratingCount = 0;
    }
    await setDoc(doc(db, 'users', uid), patch, { merge: true });
    return getUser(uid);
  });
};

export const updateUserProfile = async (uid, data) => {
  await setDoc(doc(db, 'users', uid), { ...data, updatedAt: serverTimestamp() }, { merge: true });
};

export const listenToUserProfile = (uid, callback) =>
  onSnapshot(doc(db, 'users', uid), (snap) => {
    callback(
      snap.exists() ? { id: snap.id, ...snap.data() } : null,
      {
        fromCache: snap.metadata.fromCache,
        hasPendingWrites: snap.metadata.hasPendingWrites,
        authoritative: !snap.metadata.fromCache && !snap.metadata.hasPendingWrites,
      },
    );
  });

// Keep one extra document solely to determine whether the history has a next
// page. The UI receives only the page size, so it never offers “Load older
// rides” when the loaded list is already complete.
const USER_RIDES_PAGE_SIZE = 40;

// AppContext only needs the current trip + a few recent rides (active-ride
// detection, name-heal, the Book screen's "recent places" chips). It used to
// stream 41 docs on every single app open — most of them dead history a
// returning rider never looks at. The Rides tab fetches its own full page when
// opened, and RideDetails fetches a single ride on demand.
export const listenToUserRides = (customerId, callback, onError, { pageSize = USER_RIDES_PAGE_SIZE } = {}) => {
  const size = Math.max(1, Math.min(50, Number(pageSize) || USER_RIDES_PAGE_SIZE));
  // Newest first so an active trip isn't dropped by an unordered limit.
  const q = query(
    collection(db, 'rides'),
    where('customerId', '==', customerId),
    orderBy('createdAt', 'desc'),
    limit(size + 1),
  );
  let unsub = onSnapshot(
    q,
    (snap) => {
      const docs = snap.docs.slice(0, size);
      callback(
        docs.map((d) => ({ id: d.id, ...d.data() })),
        { hasMore: snap.docs.length > size },
      );
    },
    (err) => {
      console.warn('listenToUserRides ordered query failed, falling back:', err?.code, err?.message);
      try { unsub?.(); } catch { /* */ }
      const fallback = query(
        collection(db, 'rides'),
        where('customerId', '==', customerId),
        limit(size + 1),
      );
      unsub = onSnapshot(
        fallback,
        (snap) => {
          const hasMore = snap.docs.length > size;
          const rides = snap.docs
            .slice(0, size)
            .map((d) => ({ id: d.id, ...d.data() }));
          rides.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          callback(rides, { hasMore });
        },
        (err2) => {
          console.error('listenToUserRides:', err2?.code, err2?.message);
          onError?.(err2);
        },
      );
    },
  );
  return () => {
    try { unsub?.(); } catch { /* */ }
  };
};

/** Load an older history page without disturbing the live latest-rides listener. */
export const getOlderUserRides = async (customerId, beforeMillis, pageSize = 40) => {
  if (!customerId || !Number.isFinite(Number(beforeMillis))) return { rides: [], hasMore: false };
  const safePageSize = Math.max(1, Math.min(50, Number(pageSize) || 40));
  try {
    const q = query(
      collection(db, 'rides'),
      where('customerId', '==', customerId),
      where('createdAt', '<', Timestamp.fromMillis(Number(beforeMillis))),
      orderBy('createdAt', 'desc'),
      limit(safePageSize + 1),
    );
    const snap = await getDocs(q);
    const docs = snap.docs.slice(0, safePageSize);
    return {
      rides: docs.map((d) => ({ id: d.id, ...d.data() })),
      hasMore: snap.docs.length > safePageSize,
    };
  } catch (err) {
    console.warn('getOlderUserRides failed:', err?.code, err?.message);
    return { rides: [], hasMore: false, error: err };
  }
};

/**
 * Lifetime ride totals for the My Rides header, computed server-side so the
 * numbers stay fixed regardless of how many history pages are loaded into the
 * list. Falls back to null on any failure so the UI can show an unavailable
 * state instead of presenting partial page totals as lifetime totals.
 */
export const getUserRideStats = async () => {
  try {
    const fn = httpsCallable(functions, 'getUserRideStats');
    const result = await fn({});
    const completedCount = Number(result?.data?.completedCount);
    const distanceKm = Number(result?.data?.distanceKm);
    if (!Number.isFinite(completedCount) || !Number.isFinite(distanceKm)) return null;
    return {
      completedCount: Math.max(0, Math.floor(completedCount)),
      distanceKm: Math.max(0, distanceKm),
    };
  } catch (err) {
    console.warn('getUserRideStats failed:', err?.code, err?.message);
    return null;
  }
};

const ACTIVE_RIDE_STATUSES = ['searching', 'driver_assigned', 'arrived', 'started'];

/** One-shot lookup when Book must open the existing trip after createRide reject. */
export const findActiveRideId = async (customerId) => {
  if (!customerId) return null;
  try {
    const q = query(
      collection(db, 'rides'),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc'),
      limit(40),
    );
    const snap = await getDocs(q);
    const hit = snap.docs.find((d) => ACTIVE_RIDE_STATUSES.includes(d.data()?.status));
    return hit?.id || null;
  } catch {
    try {
      const q = query(
        collection(db, 'rides'),
        where('customerId', '==', customerId),
        limit(80),
      );
      const snap = await getDocs(q);
      const rides = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rides.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      return rides.find((r) => ACTIVE_RIDE_STATUSES.includes(r.status))?.id || null;
    } catch (e2) {
      console.warn('findActiveRideId failed:', e2?.message || e2);
      return null;
    }
  }
};

export const listenToRide = (rideId, callback, onError) =>
  onSnapshot(
    doc(db, 'rides', rideId),
    (snap) => {
      callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    },
    (err) => {
      console.error('listenToRide:', err?.code, err?.message);
      onError?.(err);
    },
  );

export const getRide = async (rideId) => {
  const snap = await getDoc(doc(db, 'rides', rideId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/** Bypass local cache — use when realtime may be stale (e.g. after driver accept). */
/**
 * Strict server read — NO cache fallback. When the WebView Firestore channel
 * has stalled for a whole trip the cached copy is the stale thing, so the
 * captain-identity recovery in ActiveRide needs a read that only succeeds
 * against the server (and throws otherwise so the caller can retry).
 */
export const getRideFromServerStrict = async (rideId) => {
  const snap = await getDocFromServer(doc(db, 'rides', rideId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getRideFresh = async (rideId) => {
  try {
    const snap = await getDocFromServer(doc(db, 'rides', rideId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch {
    return getRide(rideId);
  }
};

export const listenToRideRequest = (rideId, callback, onError) => {
  if (!rideId) return () => {};
  const reqRef = ref(rtdb, `rideRequests/${rideId}`);
  return onValue(
    reqRef,
    (snap) => callback(snap.exists() ? snap.val() : null),
    (err) => {
      console.error('listenToRideRequest:', err?.code, err?.message);
      onError?.(err);
    },
  );
};

/** Fetch the server-authoritative fare before the passenger confirms. */
export const getRideQuote = async (rideData) => {
  const fn = httpsCallable(functions, 'getRideQuote');
  const payload = {
    pickup: rideData.pickup,
    destination: rideData.destination,
  };
  if (rideData.distanceKm != null && Number.isFinite(Number(rideData.distanceKm))) {
    payload.distanceKm = Number(rideData.distanceKm);
  }
  const result = await fn(payload);
  return parseRideQuote(result.data);
};

export const createRide = async (uid, rideData) => {
  if (!uid) throw new Error('not_signed_in');
  const paymentMode = rideData.paymentMode || 'cash';
  if (paymentMode !== 'cash') {
    throw new Error('use_server_payment');
  }

  const fn = httpsCallable(functions, 'createRide');
  const payload = buildCreateRidePayload(rideData);
  const result = await fn(payload);
  const data = result.data || {};
  if (data.id && data.otp) {
    cacheRideOtp(data.id, data.otp);
    logAnalyticsEvent('ride_created', { fare: data.fare, distance_km: data.distanceKm });
  }
  // CF already wrote rideRequests via Admin SDK
  return { id: data.id, otp: data.otp, fare: data.fare, distanceKm: data.distanceKm, shareToken: data.shareToken };
};

// The rider's note for the Anna (rides/{id}/note/rider; utils/riderNote.js).
// Calls back with null when there is none or it can't be read.
export const listenToRiderNote = (rideId, callback) => {
  if (!rideId) return () => {};
  return onSnapshot(
    doc(db, 'rides', rideId, 'note', 'rider'),
    (snap) => callback(snap.exists() ? snap.data() : null),
    (err) => {
      console.warn('listenToRiderNote:', err?.code);
      callback(null);
    },
  );
};

// Intertown remains isolated from local rides. Curated routes are priced from
// Admin's fixed km; custom map-picked trips include the selected points and a
// client Directions refinement, all revalidated by the server.
export const getIntertownQuote = async ({ routeId, tripType, pickup, destination, distanceKm, durationMin }) => {
  const result = await httpsCallable(functions, 'getIntertownQuote')({
    routeId, tripType, pickup, destination, distanceKm, durationMin,
  });
  return result.data || {};
};

export const createIntertownRequest = async ({ routeId, tripType, pickup, destination, distanceKm, durationMin, pickupNote, customerName, exactDrop }) => {
  const result = await httpsCallable(functions, 'createIntertownRequest')({
    routeId, tripType, pickup, destination, distanceKm, durationMin, pickupNote, customerName,
    // Only sent for a rider's own spot inside a popular town (the server then judges arrival within 350 m).
    ...(exactDrop === true ? { exactDrop: true } : {}),
  });
  return result.data || {};
};

export const cancelIntertownRequest = async (requestId, reason = '') => {
  const result = await httpsCallable(functions, 'cancelIntertownRequest')({ requestId, reason });
  return result.data || {};
};

export const listenToUserIntertownRequests = (uid, callback) => {
  if (!uid) return () => {};
  return onSnapshot(query(
    collection(db, 'intertownRequests'), where('customerId', '==', uid), orderBy('createdAt', 'desc'), limit(12),
  ), (snap) => {
    const rows = snap.docs.map((item) => ({ id: item.id, ...item.data() }));
    rows.sort((a, b) => {
      const aMs = a?.createdAt?.toMillis?.() || 0;
      const bMs = b?.createdAt?.toMillis?.() || 0;
      return bMs - aMs;
    });
    callback(rows);
  });
};

/** One Intertown request by id (the receipt page, for a trip outside the few the app keeps live). */
export const getIntertownRequest = async (requestId) => {
  const snap = await getDoc(doc(db, 'intertownRequests', requestId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const cancelRide = async (rideId, cancelReason = '') => {
  await updateDoc(doc(db, 'rides', rideId), {
    status: 'cancelled',
    cancelledBy: 'passenger',
    cancelReason: String(cancelReason || 'other').slice(0, 200),
    cancelledAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  try {
    await update(ref(rtdb, `rideRequests/${rideId}`), {
      status: 'cancelled',
      updatedAt: Date.now(),
    });
  } catch (e) {
    console.warn('rideRequest cancel mirror failed', e?.code || e?.message);
  }
};

export const submitRideRating = async (rideId, { rating, comment = '', tip = 0, tags = [] }) => {
  const tipAmount = Math.max(0, Math.min(500, Math.round(Number(tip) || 0)));
  const cleanTags = (Array.isArray(tags) ? tags : [])
    .map((k) => String(k || '').slice(0, 32))
    .filter(Boolean)
    .slice(0, 8);
  await updateDoc(doc(db, 'rides', rideId), {
    rating: Math.min(5, Math.max(1, Number(rating) || 5)),
    ratingComment: String(comment || '').slice(0, 300),
    ratingTags: cleanTags,
    tip: tipAmount,
    ratedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

/** Passenger reports whether the physical auto plate matched (ops / captain quality). */
export const reportVehicleMatch = async (rideId, matched, plate = '') => {
  await updateDoc(doc(db, 'rides', rideId), {
    vehicleMatch: matched === true,
    vehicleMatchPlate: String(plate || '').trim().toUpperCase().slice(0, 32),
    vehicleMatchAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Log any passenger interaction as a timestamped event in rides/{rideId}/events.
 * Completely non-blocking — never throws to the caller.
 *
 * @param {string} rideId
 * @param {string} type   e.g. 'driver_called', 'sos_opened', 'otp_copied'
 * @param {object} [data] optional extra payload
 */
export const logRideEvent = async (rideId, type, data = {}) => {
  if (!rideId || !type) return;
  try {
    await addDoc(collection(db, 'rides', rideId, 'events'), {
      type,
      ...data,
      at: serverTimestamp(),
    });
  } catch { /* non-blocking — never surface to UI */ }
};

const rideOtpKey = (rideId) => `mt_ride_otp_${rideId}`;

export const cacheRideOtp = (rideId, otp) => {
  if (!rideId || !otp) return;
  const value = String(otp).replace(/\D/g, '').slice(0, 4);
  if (value.length !== 4) return;
  try {
    sessionStorage.setItem(rideOtpKey(rideId), value);
    localStorage.setItem(rideOtpKey(rideId), value);
  } catch { /* private mode */ }
};

export const getCachedRideOtp = (rideId) => {
  try {
    return sessionStorage.getItem(rideOtpKey(rideId))
      || localStorage.getItem(rideOtpKey(rideId))
      || '';
  } catch {
    return '';
  }
};

export const listenToRideOtp = (rideId, callback, onError) => {
  if (!rideId) return () => {};
  const cached = getCachedRideOtp(rideId);
  if (cached) callback(cached);

  return onSnapshot(
    doc(db, 'rides', rideId, 'secrets', 'trip'),
    (snap) => {
      const live = snap.exists() ? String(snap.data()?.otp || '') : '';
      const value = live || getCachedRideOtp(rideId);
      if (live) cacheRideOtp(rideId, live);
      callback(value || null);
    },
    (err) => {
      console.error('listenToRideOtp:', err?.code, err?.message);
      const fallback = getCachedRideOtp(rideId);
      if (fallback) callback(fallback);
      onError?.(err);
    },
  );
};

// Same trip-start OTP pattern as a local ride, for Intertown's
// intertownRequests/{id}/secrets/trip instead of rides/{id}/secrets/trip.
const intertownOtpKey = (requestId) => `mt_intertown_otp_${requestId}`;

export const cacheIntertownOtp = (requestId, otp) => {
  if (!requestId || !otp) return;
  const value = String(otp).replace(/\D/g, '').slice(0, 4);
  if (value.length !== 4) return;
  try {
    sessionStorage.setItem(intertownOtpKey(requestId), value);
    localStorage.setItem(intertownOtpKey(requestId), value);
  } catch { /* private mode */ }
};

export const getCachedIntertownOtp = (requestId) => {
  try {
    return sessionStorage.getItem(intertownOtpKey(requestId))
      || localStorage.getItem(intertownOtpKey(requestId))
      || '';
  } catch {
    return '';
  }
};

export const listenToIntertownOtp = (requestId, callback, onError) => {
  if (!requestId) return () => {};
  const cached = getCachedIntertownOtp(requestId);
  if (cached) callback(cached);

  return onSnapshot(
    doc(db, 'intertownRequests', requestId, 'secrets', 'trip'),
    (snap) => {
      const live = snap.exists() ? String(snap.data()?.otp || '') : '';
      const value = live || getCachedIntertownOtp(requestId);
      if (live) cacheIntertownOtp(requestId, live);
      callback(value || null);
    },
    (err) => {
      console.error('listenToIntertownOtp:', err?.code, err?.message);
      const fallback = getCachedIntertownOtp(requestId);
      if (fallback) callback(fallback);
      onError?.(err);
    },
  );
};

export const getDriverProfile = async (driverId) => {
  if (!driverId) return null;
  const snap = await getDoc(doc(db, 'drivers', driverId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const listenToDriverProfile = (driverId, callback) => {
  if (!driverId) return () => {};
  return onSnapshot(doc(db, 'drivers', driverId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
};

export const sendRideMessage = async (rideId, senderId, text, senderRole) => {
  const cleaned = String(text || '').trim().slice(0, 480);
  if (!cleaned) throw new Error('empty');
  await addDoc(collection(db, 'rides', rideId, 'messages'), {
    senderId,
    senderRole,
    text: cleaned,
    createdAt: serverTimestamp(),
  });
};

export const listenToRideMessages = (rideId, callback) => {
  const q = query(
    collection(db, 'rides', rideId, 'messages'),
    orderBy('createdAt', 'asc'),
    limit(100),
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const listenToPlatformConfig = (callback) =>
  onSnapshot(doc(db, 'config', 'platform'), { includeMetadataChanges: true }, (snap) => {
    callback(snap.exists() ? snap.data() : null, { fromServer: !snap.metadata.fromCache });
  }, (error) => callback(null, { error }));

/**
 * Saves this device's notification address. With `device` ({ deviceId,
 * platform, appVersion }, utils/pushDevices.js) it is kept as this device's
 * own entry in pushDevices, so other devices on the account keep theirs; the
 * single fcmToken is still written for old servers and the admin broadcast.
 */
export const saveFcmToken = async (uid, token, device = null) => {
  if (!uid || !token) return;
  const ref = doc(db, 'users', uid);
  const legacy = () => setDoc(ref, { fcmToken: token, updatedAt: serverTimestamp() }, { merge: true });
  if (!device?.deviceId) {
    await legacy();
    return;
  }
  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const pushDevices = nextPushDevices(snap.exists() ? snap.data()?.pushDevices : null, device.deviceId, {
        token,
        platform: device.platform || 'web',
        appVersion: String(device.appVersion || ''),
        updatedAt: serverTimestamp(),
      });
      tx.set(ref, { fcmToken: token, pushDevices, updatedAt: serverTimestamp() },
        { mergeFields: ['fcmToken', 'pushDevices', 'updatedAt'] });
    });
  } catch (e) {
    // Never worse than before: at least the single address is saved.
    console.warn('saveFcmToken device entry failed, saving fcmToken only:', e?.message);
    await legacy();
  }
};

export const saveSavedPlace = async (uid, key, place) => {
  if (!['home', 'work'].includes(key)) throw new Error('invalid_place');
  const value = place
    ? {
      name: place.name || place.address,
      address: place.address || place.name,
      lat: place.lat,
      lng: place.lng,
    }
    : null;
  await updateDoc(doc(db, 'users', uid), {
    [`savedPlaces.${key}`]: value,
    updatedAt: serverTimestamp(),
  });
};

export const listenToDriverLocation = (driverId, callback, onError) => {
  if (!driverId) return () => {};
  const locRef = ref(rtdb, `driverLocations/${driverId}`);
  return onValue(
    locRef,
    (snap) => callback(snap.exists() ? snap.val() : null),
    (err) => {
      console.error('listenToDriverLocation:', err?.code, err?.message);
      onError?.(err);
    },
  );
};

/**
 * Live assigned-trip GPS — rideLocations/{rideId} (participants only).
 * Does NOT read driverLocations/* (fleet locked down).
 */
export const listenToRideDriverLocation = (rideId, callback, onError) => {
  if (!rideId) return () => {};
  const locRef = ref(rtdb, `rideLocations/${rideId}`);
  return onValue(
    locRef,
    (snap) => callback(snap.exists() ? snap.val() : null),
    (err) => {
      console.error('listenToRideDriverLocation:', err?.code, err?.message);
      onError?.(err);
    },
  );
};

/**
 * One-shot rideLocations read — poll/foreground fallback for when the onValue
 * WebSocket stalls in the Android WebView (the map dot would otherwise freeze
 * with no recovery).
 */
export const getRideDriverLocationOnce = async (rideId) => {
  if (!rideId) return null;
  try {
    const snap = await rtdbGet(ref(rtdb, `rideLocations/${rideId}`));
    return snap.exists() ? snap.val() : null;
  } catch {
    return null;
  }
};

let lastNearbyPollTs = 0;

/**
 * Backend-mediated nearby captains. Polls getNearbyDrivers — no client fleet read.
 */
export const pollNearbyDrivers = (pickup, onUpdate, onError, opts = {}) => {
  if (!pickup?.lat || !pickup?.lng) return () => {};
  let cancelled = false;
  let inFlight = false;
  const fastIntervalMs = Math.max(4500, Number(opts.intervalMs) || 5000);
  const slowIntervalMs = Math.max(fastIntervalMs, Number(opts.slowIntervalMs) || 12000);
  const fastWindowMs = Math.max(0, Number(opts.fastWindowMs) || 60000);
  const radiusKm = opts.radiusKm;

  const tick = async () => {
    if (cancelled || inFlight) return;
    const now = Date.now();
    if (now - lastNearbyPollTs < 2500) return;
    lastNearbyPollTs = now;
    inFlight = true;
    try {
      const fn = httpsCallable(functions, 'getNearbyDrivers');
      const result = await fn({
        lat: pickup.lat,
        lng: pickup.lng,
        radiusKm: radiusKm != null ? radiusKm : undefined,
      });
      if (!cancelled) onUpdate(result.data?.drivers || []);
    } catch (err) {
      if (err?.code === 'resource-exhausted' || err?.message?.includes('Too many requests') || err?.code === 'functions/resource-exhausted') {
        // Backoff quietly on rate limit
        return;
      }
      console.error('pollNearbyDrivers:', err?.code, err?.message);
      if (!cancelled) onError?.(err);
    } finally {
      inFlight = false;
    }
  };

  tick();
  // Quick refresh for the first minute (feels instant while the passenger is
  // actively watching the search screen), then step down to a slower cadence
  // — a search that's still running past that point is being waited on, not
  // watched pixel-by-pixel, and this is purely cosmetic nearby-icon refresh,
  // fully separate from the actual ride-matching pipeline.
  let id = setInterval(tick, fastIntervalMs);
  const stepDown = fastWindowMs > 0 ? setTimeout(() => {
    if (cancelled) return;
    clearInterval(id);
    id = setInterval(tick, slowIntervalMs);
  }, fastWindowMs) : null;

  return () => {
    cancelled = true;
    clearInterval(id);
    if (stepDown) clearTimeout(stepDown);
  };
};

/** @deprecated Fleet listen removed — use pollNearbyDrivers. */
export const listenToAllDriverLocations = (callback, onError) => {
  console.warn('listenToAllDriverLocations disabled — use pollNearbyDrivers');
  onError?.(new Error('fleet_listen_disabled'));
  callback([]);
  return () => {};
};

export const listenToRideShare = (token, callback, onError) => {
  if (!token) return () => {};
  return onSnapshot(
    doc(db, 'rideShares', token),
    (snap) => callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => {
      console.error('listenToRideShare:', err?.code, err?.message);
      onError?.(err);
    },
  );
};

export const listenToPublicTrack = (token, callback, onError) => {
  if (!token) return () => {};
  const trackRef = ref(rtdb, `publicTracks/${token}`);
  return onValue(
    trackRef,
    (snap) => callback(snap.exists() ? snap.val() : null),
    (err) => {
      console.error('listenToPublicTrack:', err?.code, err?.message);
      onError?.(err);
    },
  );
};

/**
 * How far this device's clock is from Firebase's (ms; add it to Date.now()).
 * Lets the share page age the server-stamped live ETA correctly even when a
 * family member's phone clock is a few minutes off.
 */
export const listenToServerTimeOffset = (callback) => onValue(
  ref(rtdb, '.info/serverTimeOffset'),
  (snap) => callback(Number(snap.val()) || 0),
  () => callback(0),
);

export const ensureRideShare = async (rideId) => {
  const fn = httpsCallable(functions, 'ensureRideShare');
  const result = await fn({ rideId, origin: PUBLIC_ORIGIN() });
  return result.data;
};

export const triggerRideSos = async (rideId) => {
  const fn = httpsCallable(functions, 'triggerRideSos');
  const result = await fn({ rideId, origin: PUBLIC_ORIGIN() });
  return result.data;
};

// The same two for an Intertown trip (firebase/functions/intertownSafety.js): the family's live link,
// and the SOS that reaches MyTownly. Both work only while the trip is live.
export const ensureIntertownShare = async (requestId) => {
  const fn = httpsCallable(functions, 'ensureIntertownShare');
  const result = await fn({ requestId, origin: PUBLIC_ORIGIN() });
  return result.data;
};

export const triggerIntertownSos = async (requestId) => {
  const fn = httpsCallable(functions, 'triggerIntertownSos');
  const result = await fn({ requestId, origin: PUBLIC_ORIGIN() });
  return result.data;
};

export const createSupportCase = async ({ rideId = '', category = 'other', notes = '' } = {}) => {
  const fn = httpsCallable(functions, 'createSupportCase');
  const result = await fn({ rideId, category, notes });
  return result.data;
};

export const submitAccountDeletionRequest = async ({ phone, reason = '', details = '', role = 'passenger' }) => {
  const fn = httpsCallable(functions, 'requestAccountDeletion');
  const result = await fn({ phone, reason, details, role });
  return result.data;
};

export const recordConsent = async (uid, { purposes, noticeVersion, method }) => {
  if (!uid) return;
  await addDoc(collection(db, 'consentRecords'), {
    uid,
    purposes: Array.isArray(purposes) ? purposes.slice(0, 12) : [],
    noticeVersion: String(noticeVersion || '').slice(0, 40),
    method: String(method || 'login').slice(0, 40),
    createdAt: serverTimestamp(),
  });
};

export const savePrivacyPreferences = async (uid, _role, { analyticsOptOut, marketingOptIn }) => {
  if (!uid) return;
  try {
    localStorage.setItem('mt_analytics_opt_out', analyticsOptOut ? '1' : '0');
  } catch { /* */ }
  await setDoc(doc(db, 'users', uid), {
    analyticsOptOut: Boolean(analyticsOptOut),
    marketingOptIn: Boolean(marketingOptIn),
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const saveNominee = async (uid, _role, nominee) => {
  if (!uid) return;
  await setDoc(doc(db, 'users', uid), {
    nominee: nominee
      ? {
          name: String(nominee.name || '').slice(0, 60),
          phone: String(nominee.phone || '').replace(/\D/g, '').slice(-10),
        }
      : null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const submitPrivacyRequest = async ({ type, details = '' }) => {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('not_signed_in');
  await addDoc(collection(db, 'privacyRequests'), {
    uid,
    type: String(type || 'access').slice(0, 40),
    details: String(details || '').slice(0, 1000),
    status: 'pending',
    createdAt: serverTimestamp(),
  });
};

export const attestAgeAndConsent = async (uid, noticeVersion) => {
  if (!uid) return;
  await setDoc(doc(db, 'users', uid), {
    ageAttested: true,
    ageAttestedAt: serverTimestamp(),
    privacyNoticeVersionAccepted: String(noticeVersion || '').slice(0, 40),
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const boostRideFare = async (rideId, boostAmount) => {
  const fn = httpsCallable(functions, 'boostRideFare');
  const res = await fn({ rideId, boostAmount });
  return res.data;
};

export const keepWaitingRide = async (rideId) => {
  const fn = httpsCallable(functions, 'keepWaitingRide');
  const res = await fn({ rideId });
  return res.data;
};

export { getMessaging, getToken, isSupported, onMessage };
